import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, type StocktakeSession } from '../api';
import { canWrite, useAuth } from '../auth';
import { LocationCsvImport } from '../components/LocationCsvImport';
import { useLocations } from '../queries';
import { Button, Card, Empty, ErrorMessage, Field, Input, PageHeader, Spinner, number, useConfirm } from '../ui';

export function Locations() {
  const { user } = useAuth();
  const writable = canWrite(user);
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const navigate = useNavigate();
  const locations = useLocations();
  const [code, setCode] = useState('');
  const [warehouse, setWarehouse] = useState('main');
  const [walkOrder, setWalkOrder] = useState('');
  const [editing, setEditing] = useState<number | null>(null);
  const [edit, setEdit] = useState({ code: '', warehouse: '', walkOrder: '' });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['locations'] });
    void qc.invalidateQueries({ queryKey: ['lots'] });
  };
  const create = useMutation({
    mutationFn: () => api('/api/v1/locations', { body: { code, warehouse: warehouse || 'main', walkOrder: Number(walkOrder) || 0 } }),
    onSuccess: () => {
      setCode('');
      setWalkOrder('');
      refresh();
    },
  });
  const save = useMutation({
    mutationFn: (id: number) => api(`/api/v1/locations/${id}`, { method: 'PATCH', body: { code: edit.code, warehouse: edit.warehouse || 'main', walkOrder: Number(edit.walkOrder) || 0 } }),
    onSuccess: () => {
      setEditing(null);
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: number) => api(`/api/v1/locations/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });
  const count = useMutation({
    mutationFn: (id: number) => api<{ session: StocktakeSession }>('/api/v1/stocktaking', { body: { locationId: id } }).then((r) => r.session),
    onSuccess: (session) => navigate(`/stocktaking/${session.id}`),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    create.mutate();
  }

  return (
    <>
      {dialog}
      <PageHeader
        title="Locations"
        subtitle="Bins, drawers and shelves. The walk order is the order a picker visits them, so a pick route follows the shop floor."
      />
      <div className="space-y-4">
        {writable && (
          <Card title="Add a location">
            <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
              <Field label="Code" hint="Short and unique, e.g. A1 or Shed-3">
                <Input required value={code} onChange={(e) => setCode(e.target.value)} />
              </Field>
              <Field label="Warehouse">
                <Input value={warehouse} onChange={(e) => setWarehouse(e.target.value)} />
              </Field>
              <Field label="Walk order" hint="Lower numbers first">
                <Input inputMode="numeric" value={walkOrder} onChange={(e) => setWalkOrder(e.target.value)} />
              </Field>
              <Button type="submit" disabled={!code.trim() || create.isPending}>
                Add
              </Button>
            </form>
            <div className="mt-3">
              <ErrorMessage error={create.error} />
            </div>
          </Card>
        )}
        {writable && <LocationCsvImport />}
        <ErrorMessage error={remove.error ?? save.error} />
        {locations.isPending ? (
          <Spinner />
        ) : locations.data && locations.data.length === 0 ? (
          <Empty>No locations yet.</Empty>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
            <table className="data w-full min-w-[44rem] text-sm">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Inside</th>
                  <th>Warehouse</th>
                  <th className="text-right">Walk order</th>
                  <th className="text-right">Lots</th>
                  <th className="text-right">Pieces</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {locations.data?.map((l) =>
                  editing === l.id ? (
                    <tr key={l.id}>
                      <td>
                        <Input aria-label="Code" value={edit.code} onChange={(e) => setEdit({ ...edit, code: e.target.value })} />
                      </td>
                      <td colSpan={2} />
                      <td>
                        <Input aria-label="Warehouse" value={edit.warehouse} onChange={(e) => setEdit({ ...edit, warehouse: e.target.value })} />
                      </td>
                      <td>
                        <Input aria-label="Walk order" className="text-right" value={edit.walkOrder} onChange={(e) => setEdit({ ...edit, walkOrder: e.target.value })} />
                      </td>
                      <td colSpan={2} />
                      <td className="space-x-2 text-right whitespace-nowrap">
                        <Button small onClick={() => save.mutate(l.id)}>
                          Save
                        </Button>
                        <Button small variant="ghost" onClick={() => setEditing(null)}>
                          Cancel
                        </Button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={l.id}>
                      <td className="font-medium">
                        <Link to={`/items?view=lots&location=${encodeURIComponent(l.code)}`} className="hover:underline">
                          {l.code}
                        </Link>
                      </td>
                      <td>{l.name ?? <span className="text-slate-400">—</span>}</td>
                      <td>{locations.data?.find((p) => p.id === l.parentId)?.code ?? <span className="text-slate-400">—</span>}</td>
                      <td>{l.warehouse}</td>
                      <td className="text-right tabular-nums">{l.walkOrder}</td>
                      <td className="text-right tabular-nums">{number(l.lots)}</td>
                      <td className="text-right tabular-nums">{number(l.pieces)}</td>
                      <td className="space-x-2 text-right whitespace-nowrap">
                        {writable && (
                          <>
                            {l.openSessionId ? (
                              <Button small variant="secondary" onClick={() => navigate(`/stocktaking/${l.openSessionId}`)}>
                                Resume count
                              </Button>
                            ) : (
                              <Button small variant="secondary" disabled={count.isPending} onClick={() => count.mutate(l.id)}>
                                Count this bin
                              </Button>
                            )}
                            <Button
                              small
                              variant="secondary"
                              onClick={() => {
                                setEditing(l.id);
                                setEdit({ code: l.code, warehouse: l.warehouse, walkOrder: String(l.walkOrder) });
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              small
                              variant="ghost"
                              disabled={l.pieces > 0}
                              title={l.pieces > 0 ? 'Move its stock out first' : undefined}
                              onClick={async () => (await confirm(`Delete location ${l.code}?`, { label: 'Delete', danger: true })) && remove.mutate(l.id)}
                            >
                              Delete
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
